import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { AuthContext } from '@/contexts/AuthContextDefinition';
import { supabase } from '@/integrations/supabase/client';
import {
  PREVIEW_ACCOUNT_MESSAGE,
  PREVIEW_ACCOUNT_PLAN_LABEL,
  setPreviewAccountFlag,
} from '@/lib/preview-account';

export const PREVIEW_ACCOUNT_QUERY_KEY = ['account', 'preview'] as const;

const MISSING_TABLE_CODES = new Set(['42P01', 'PGRST205']);

export function usePreviewAccount() {
  const userId = useContext(AuthContext)?.user?.id;
  const query = useQuery<boolean>({
    queryKey: [...PREVIEW_ACCOUNT_QUERY_KEY, userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('preview_accounts')
        .select('owner_id')
        .limit(1)
        .maybeSingle();
      if (error) {
        if (error.code && MISSING_TABLE_CODES.has(error.code)) return false;
        throw error;
      }
      return data !== null;
    },
    enabled: Boolean(userId),
    staleTime: 10 * 60 * 1000,
  });

  const isPreviewAccount = query.data === true;

  useEffect(() => {
    setPreviewAccountFlag(isPreviewAccount);
  }, [isPreviewAccount]);

  return {
    isPreviewAccount,
    isLoading: query.isPending && Boolean(userId),
    message: PREVIEW_ACCOUNT_MESSAGE,
    planLabel: PREVIEW_ACCOUNT_PLAN_LABEL,
  };
}
